import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { queryKeys } from './query-client';
import { useAgentTaskDuplexer } from './use-agent-task-duplexer';
import type { WebConversationDecision, WebConversationDecisions } from '../pages/api/conversation-decisions';

/**
 * The OPEN questions this conversation raised — so a brief can be ANSWERED where it asks.
 *
 * The rows are the same `WebLedgerDecision` the goal panel already renders through
 * `DecisionAnswerBlock`; only the way they are found differs (by conversation, not by goal). That is the
 * whole point: a second answering mechanism is what produced the bug this closes — a question answered in
 * chat resolved the turn and settled no row, so the next morning asked it again.
 *
 * **An empty list and a failed read are the same thing HERE, deliberately** — and that is the opposite of
 * `useGoalLedger`'s rule, so it is worth saying why. The ledger renders a section that must not claim
 * "nothing happened" when it could not look. This renders nothing at all when there is nothing to answer,
 * so a failed read degrades to the prose the brief has always been. There is no empty state to lie with.
 *
 * A verdict lands as a task write, so the agent-task duplexer refetches — an answered question has to
 * leave the card, or the reader answers it twice.
 */
export function useConversationDecisions(conversationId: string | undefined): WebConversationDecision[] {
  const query = useQuery({
    queryKey: queryKeys.conversationDecisions(conversationId),
    queryFn: () => fetchConversationDecisions(conversationId as string),
    enabled: !!conversationId,
  });

  useAgentTaskDuplexer(() => {
    if (conversationId) void query.refetch();
  });

  return query.data?.decisions ?? [];
}

async function fetchConversationDecisions(conversationId: string): Promise<WebConversationDecisions> {
  const params = new URLSearchParams({ conversationId });
  const res = await apiFetch(`${API_BASE}/api/conversation-decisions?${params.toString()}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = (await res.json()) as Partial<WebConversationDecisions>;
  return { decisions: data.decisions ?? [] };
}
